import type { ShortcutCatalogueEntry } from '../services/hotkeys.service';

/**
 * Pure helpers for rendering keyboard shortcuts in UI (the ⌘/ dialog).
 * Kept free of Angular imports so they're trivially unit-testable.
 */

/**
 * Split a normalised binding key ('mod+k', 'mod+shift+w', 'mod+/') into
 * displayable key-cap parts for the active platform:
 *   mac:   'mod+k' → ['⌘', 'K']
 *   other: 'mod+k' → ['Ctrl', 'K']
 */
export function formatShortcutKeys(key: string, isMac: boolean): string[] {
  return key.split('+').map(part => {
    switch (part) {
      case 'mod': return isMac ? '⌘' : 'Ctrl';
      case 'shift': return isMac ? '⇧' : 'Shift';
      case 'alt': return isMac ? '⌥' : 'Alt';
      default: return part.length === 1 ? part.toUpperCase() : part;
    }
  });
}

/** A dialog row: one description with every key combo bound to it. */
export interface ShortcutRow {
  descriptionKey: string;
  descriptionLabelKey?: string;
  group: 'navigation' | 'actions';
  /** One inner array per bound combo, e.g. [['⌘','K'], ['⌘','P']]. */
  combos: string[][];
}

/**
 * Collapse the raw catalogue into display rows, merging bindings that share
 * the same description (⌘K and ⌘P both open the command palette) so the
 * dialog reads as one action with alternative keys. Preserves registration
 * order within each group.
 */
export function buildShortcutRows(
  entries: readonly ShortcutCatalogueEntry[],
  isMac: boolean,
): ShortcutRow[] {
  const rows = new Map<string, ShortcutRow>();
  for (const entry of entries) {
    const id = `${entry.descriptionKey}|${entry.descriptionLabelKey ?? ''}`;
    const combo = formatShortcutKeys(entry.key, isMac);
    const existing = rows.get(id);
    if (existing) {
      existing.combos.push(combo);
    } else {
      rows.set(id, {
        descriptionKey: entry.descriptionKey,
        descriptionLabelKey: entry.descriptionLabelKey,
        group: entry.group,
        combos: [combo],
      });
    }
  }
  return [...rows.values()];
}
